/**
 * ConversationHistory Component
 *
 * Sidebar panel that lists the user's previous AI assistant conversations.
 * Each entry shows the conversation title, a relative timestamp (e.g., "5m ago"),
 * and a message count. The currently active thread is highlighted. Users can
 * select a conversation to resume it, delete conversations, or start a new chat.
 *
 * Used inside the Assistant feature's chat interface.
 *
 * @param {Object[]} conversations - Array of conversation summary objects
 * @param {string}   activeThreadId - ID of the currently selected conversation
 * @param {Function} onSelect - Called with threadId when a conversation is clicked
 * @param {Function} onDelete - Called with threadId to remove a conversation
 * @param {Function} onNewChat - Called to start a fresh conversation
 * @param {boolean}  loading - Whether conversations are still being fetched
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Button, ListGroup, Spinner } from 'react-bootstrap';

/**
 * Converts a Date into a human-readable relative time string.
 * Returns "Just now", "Xm ago", "Xh ago", "Xd ago", or a locale date string.
 */
function formatTimeAgo(date) {
    if (!date) return '';
    const parsed = new Date(date);
    if (isNaN(parsed.getTime()) || parsed.getTime() === 0) return '';

    const now = new Date();
    const diff = now - parsed;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return parsed.toLocaleDateString();
}

export default function ConversationHistory({
    conversations,
    activeThreadId,
    onSelect,
    onDelete,
    onNewChat,
    loading,
}) {
    return (
        <div>
            {/* Header with title and new-chat button */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 fw-bold small text-uppercase text-muted">
                    Recent Chats
                </h6>
                <Button
                    variant="outline-primary"
                    size="sm"
                    className="rounded-pill"
                    onClick={onNewChat}
                    disabled={loading}
                >
                    + New Chat
                </Button>
            </div>

            {/* Loading and empty states */}
            {loading && conversations.length === 0 && (
                <div className="text-center py-3">
                    <Spinner
                        animation="border"
                        size="sm"
                        className="text-muted"
                    />
                </div>
            )}

            {!loading && conversations.length === 0 && (
                <p className="text-muted small mb-0">No conversations yet.</p>
            )}

            {/* Conversation list: each item shows title, time, and a delete button */}
            <ListGroup variant="flush">
                {conversations.map((conv) => (
                    <ListGroup.Item
                        key={conv.threadId}
                        action
                        active={conv.threadId === activeThreadId}
                        onClick={() => onSelect(conv.threadId)}
                        className="d-flex justify-content-between align-items-start px-2 py-2 border-0"
                    >
                        <div className="me-2 overflow-hidden">
                            <div className="text-truncate small fw-medium">
                                {conv.title || 'Untitled Chat'}
                            </div>
                            <div className="text-muted small">
                                {formatTimeAgo(conv.lastActivity)}
                                {formatTimeAgo(conv.lastActivity) &&
                                    conv.messageCount > 0 &&
                                    ' \u00B7 '}
                                {conv.messageCount > 0 &&
                                    `${conv.messageCount} msg${conv.messageCount !== 1 ? 's' : ''}`}
                            </div>
                        </div>
                        <Button
                            variant="link"
                            size="sm"
                            className="text-muted p-0 flex-shrink-0"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(conv.threadId);
                            }}
                            aria-label={`Delete conversation: ${conv.title || 'Untitled Chat'}`}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                            >
                                <path
                                    d={
                                        'M4.646 4.646a.5.5 0 0 1 .708 ' +
                                        '0L8 7.293l2.646-2.647a.5.5 0 ' +
                                        '0 1 .708.708L8.707 8l2.647 ' +
                                        '2.646a.5.5 0 0 1-.708.708L8 ' +
                                        '8.707l-2.646 2.647a.5.5 0 0 ' +
                                        '1-.708-.708L7.293 8 4.646 ' +
                                        '5.354a.5.5 0 0 1 0-.708z'
                                    }
                                />
                            </svg>
                        </Button>
                    </ListGroup.Item>
                ))}
            </ListGroup>
        </div>
    );
}

ConversationHistory.propTypes = {
    conversations: PropTypes.arrayOf(
        PropTypes.shape({
            threadId: PropTypes.string.isRequired,
            title: PropTypes.string,
            lastActivity: PropTypes.instanceOf(Date),
            messageCount: PropTypes.number,
        })
    ).isRequired,
    activeThreadId: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onNewChat: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};
